import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { mdiAlertCircleOutline, mdiQrcode } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { useNavigate } from '@tanstack/react-router'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { $api } from '../../api/queryClient'
import { useDebounce } from '../../hooks/useDebounce'
import { formatAssetWithPath } from '../../lib/assetPath'
import { useMemberLabelResolve, useUpdateLabel } from '../../hooks/useLabels'

interface AssetSearchResult {
    uid: string
    name?: string
    parent_asset_uid?: string | null
    parent_chain?: boolean
}

/**
 * LabelAssignPage is the "stick first, assign by scanning" screen.
 *
 * On an un-inventoried portfolio this is the normal rollout, not a fallback:
 * the technician sticks blank labels as they walk, scans each one, and picks
 * the equipment it landed on — so the inventory gets built by the act of
 * labelling rather than having to precede it.
 *
 * The label lookup goes through the AUTHENTICATED resolve endpoint, because the
 * public one deliberately refuses to confirm that an unassigned code exists.
 */
export function LabelAssignPage({ org, code }: { org: string; code: string }) {
    const { t } = useTranslation('labels')
    const navigate = useNavigate()
    const { data: label, isLoading, error } = useMemberLabelResolve(code)

    const [search, setSearch] = useState('')
    const debounced = useDebounce(search, 300)
    const [selected, setSelected] = useState<AssetSearchResult | null>(null)

    const assign = useUpdateLabel(org)

    const { data: assets } = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/assets',
        { params: { path: { orgCode: org }, query: { q: debounced, limit: 20, asset_chain: true } } },
        { enabled: debounced.trim().length > 1 },
    )

    const allRows = useMemo(() => (assets as { data?: AssetSearchResult[] } | undefined)?.data ?? [], [assets])
    const byUid = useMemo(() => new Map(allRows.map((a) => [a.uid, a])), [allRows])
    const results = useMemo(() => allRows.filter((a) => !a.parent_chain).slice(0, 20), [allRows])

    if (isLoading) {
        return <div className="mx-auto max-w-md p-6 text-muted-foreground">{t('scan.loading')}</div>
    }

    if (error || !label) {
        return (
            <div className="mx-auto max-w-md p-6" data-testid="label-assign-not-found">
                <AppIcon tone="muted" path={mdiAlertCircleOutline} size="xl" className="mb-3" />
                <h1 className="text-xl font-semibold text-foreground">{t('scan.not_found_title')}</h1>
                <p className="mt-2 text-sm text-muted-foreground">{t('scan.not_found_body')}</p>
            </div>
        )
    }

    return (
        <div className="mx-auto max-w-md space-y-6 p-4 md:p-6" data-testid="label-assign-page">
            <div>
                <h1 className="text-xl font-semibold text-foreground">{t('assign.title', { code: label.code })}</h1>
                <p className="mt-1 text-sm text-muted-foreground">{t('assign.description')}</p>
                <p className="mt-3 flex items-center gap-1.5 font-mono text-xs text-muted-foreground">
                    <AppIcon path={mdiQrcode} size="xs" className="shrink-0" />
                    {label.code}
                </p>
            </div>

            <div className="space-y-1.5">
                <Label htmlFor="label-assign-search">{t('assign.asset')}</Label>
                <Input
                    id="label-assign-search"
                    value={search}
                    onChange={event => {
                        setSearch(event.target.value)
                        setSelected(null)
                    }}
                    data-testid="label-assign-search"
                />
            </div>

            {results.length > 0 && !selected && (
                <ul className="divide-y divide-border rounded-md border border-border" data-testid="label-assign-results">
                    {results.map(asset => (
                        <li key={asset.uid}>
                            <button
                                type="button"
                                className="w-full px-3 py-2 text-left text-sm hover:bg-muted"
                                onClick={() => setSelected({ ...asset, name: formatAssetWithPath(asset, byUid) })}
                                data-testid={`label-assign-option-${asset.uid}`}
                            >
                                {formatAssetWithPath(asset, byUid)}
                            </button>
                        </li>
                    ))}
                </ul>
            )}

            {selected && (
                <p className="rounded-md border border-border p-3 text-sm text-foreground" data-testid="label-assign-selected">
                    {selected.name || selected.uid}
                </p>
            )}

            {assign.isError && (
                <p className="text-sm text-destructive" data-testid="label-assign-error">
                    {t('assign.error')}
                </p>
            )}

            <Button
                className="w-full"
                size="lg"
                disabled={!selected || assign.isPending}
                data-testid="label-assign-submit"
                onClick={() => {
                    if (!selected) return
                    assign.mutate(
                        {
                            params: { path: { orgCode: org, labelUid: label.label_uid! } },
                            body: { asset_uid: selected.uid },
                        },
                        {
                            onSuccess: () => {
                                navigate({ to: '/$org/assets/$assetUid', params: { org, assetUid: selected.uid } })
                            },
                        },
                    )
                }}
            >
                {assign.isPending ? t('assign.assigning') : t('assign.submit')}
            </Button>
        </div>
    )
}
